import React, { useContext, useState } from 'react';
import { Project, Link, WeekData, StateContext } from '../../StateContext';
import {
    deleteProjectCall, editProjectCall, updateProjectExtrasCall, deleteLink,
} from '../../Services/APICalls/apicalls';
import { IContextData } from '../../../App';
import {
    secondsToHours, totalHours, totalProjectBaseline, hoursForType,
    totalHoursUpTo,
} from '../../General/Links/weekView';
import EditWeek from '../../General/Links/editWeek';
import AddWeekOverview from '../../General/Links/addWeekOverview';
import DeleteConfirmation from '../../General/DeleteConfirmation';
import ExpandableRow from '../../General/ExpandableRow';
import ConnectMechanic from './connectMechanic';
import EditProject from './editProject';
import ToDoList from './To-do list/toDoList';
import FilterProjectList from './filterProjectList';
import ProjectExtrasModal from './ProjectExtrasModal';
import PrintablePage from '../../General/PrintablePages';
import TotalHourOverview from '../../General/TotalHourOverview';
import { useLanguage } from '../../Services/LanguageContext';
import { Button, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

// ── helpers (same logic as projectList.tsx) ──────────────────────────────────

function getFirstDayOfISOWeek(weekNumber: number, year: number): Date {
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const week1Mon = new Date(year, 0, 4 - dayOfWeek + 1);
    const result = new Date(week1Mon);
    result.setDate(week1Mon.getDate() + (weekNumber - 1) * 7);
    return result;
}

function getProjectMonthKey(project: Project): string {
    let earliest: { weekNumber: number; year: number } | null = null;
    for (const link of project.links ?? []) {
        for (const wd of link.weekData ?? []) {
            const year = wd.year || new Date(wd.created).getFullYear();
            if (!earliest || year < earliest.year || (year === earliest.year && wd.weekNumber < earliest.weekNumber)) {
                earliest = { weekNumber: wd.weekNumber, year };
            }
        }
    }
    if (!earliest) return 'no-data';
    const date = getFirstDayOfISOWeek(earliest.weekNumber, earliest.year);
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    return `${y}-${m < 10 ? '0' + m : m}`;
}

function formatMonthKey(key: string): string {
    if (key === 'no-data') return 'Geen data';
    const [year, month] = key.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleString('nl-NL', { month: 'long', year: 'numeric' });
}

const hoursToSeconds = (h: number) => Math.round(h * 3600);

// ── status button styles (same as projectList.tsx) ───────────────────────────

const statusButton = (active: boolean, color: string): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    border: `1.5px solid ${active ? color : '#555'}`,
    background: active ? `${color}22` : '#2a2d3a',
    color: active ? color : '#aaa',
    transition: 'all 0.2s ease',
    userSelect: 'none' as const,
    boxShadow: active ? `0 0 6px ${color}44` : '0 1px 3px rgba(0,0,0,0.35)',
});

const dot = (color: string): React.CSSProperties => ({
    width: '7px', height: '7px', borderRadius: '50%', background: color, flexShrink: 0,
});

const checkbox = (active: boolean, color: string): React.CSSProperties => ({
    width: '13px', height: '13px',
    border: `1.5px solid ${active ? color : '#888'}`,
    borderRadius: '3px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '9px', flexShrink: 0,
    color: active ? color : '#888',
});

// ── ChevronIcon ───────────────────────────────────────────────────────────────

const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
    <svg
        width="13" height="13" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

// ── MechanicRow ───────────────────────────────────────────────────────────────
// Replaces LinkView + ExpandableRow + WeekView with a flat, border-free row.

interface MechanicRowProps {
    link: Link;
    project: Project;
    filterTypeId?: number | null;
}

const MechanicRow = ({ link, project, filterTypeId }: MechanicRowProps) => {
    const [expanded, setExpanded] = useState(false);
    const { setLoginData } = useContext<IContextData>(StateContext);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const displayWeekData: WeekData[] = filterTypeId !== undefined
        ? link.weekData.filter(w =>
            filterTypeId === null ? w.projectTypeId == null : w.projectTypeId === filterTypeId
          )
        : link.weekData;

    const sorted = displayWeekData.slice().sort((a, b) => a.weekNumber - b.weekNumber);
    const total = totalHours(displayWeekData);

    return (
        <div style={{ marginBottom: '2px' }}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px 6px 10px', cursor: 'pointer', borderRadius: '6px' }}
                onClick={() => setExpanded(v => !v)}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{ color: expanded ? 'var(--accent-cyan)' : 'var(--text-muted, #666)' }}>
                        <ChevronIcon isExpanded={expanded} />
                    </span>
                    <span style={{ fontWeight: 600, fontSize: '14px', color: expanded ? 'var(--accent-cyan)' : 'var(--text-primary)', transition: 'color 0.2s ease' }}>
                        {link.mechanic?.name}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={e => e.stopPropagation()}>
                    <span style={{
                        padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 700,
                        background: 'rgba(34,197,94,0.12)', color: 'var(--success)',
                    }}>
                        {total.toFixed(2)}u
                    </span>
                    <AddWeekOverview link={link} defaultTypeId={filterTypeId} />
                    <IconButton
                        size='small'
                        onClick={() => setDeleteOpen(true)}
                        sx={{ color: 'var(--text-muted, #666)', '&:hover': { color: 'var(--danger)' } }}
                    >
                        <DeleteIcon fontSize='small' />
                    </IconButton>
                    <DeleteConfirmation
                        dialogOpen={deleteOpen}
                        handleDialogAbort={e => { e.stopPropagation(); setDeleteOpen(false); }}
                        handleDialogConfirm={e => { e.stopPropagation(); deleteLink(link.id, setLoginData); setDeleteOpen(false); }}
                    />
                </div>
            </div>

            {expanded && (
                <div style={{ paddingLeft: '16px', paddingBottom: '4px' }}>
                    {sorted.length === 0 ? (
                        <div style={{ padding: '4px 0', fontSize: '12px', color: 'var(--text-muted, #666)' }}>
                            Geen weken
                        </div>
                    ) : (
                        <>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '14px',
                                padding: '3px 6px',
                                fontSize: '10px', fontWeight: 700,
                                color: 'var(--text-muted, #666)',
                                textTransform: 'uppercase', letterSpacing: '0.07em',
                                borderBottom: '1px solid var(--border-color)',
                            }}>
                                <span style={{ minWidth: '44px' }}>Week</span>
                                <span style={{ minWidth: '52px' }}>Uren</span>
                                <span style={{ minWidth: '40px', textAlign: 'center' }}>%</span>
                                <span style={{ marginLeft: 'auto' }}>Totaal</span>
                                <span style={{ width: '24px' }} />
                            </div>
                            {sorted.map(w => (
                                <div
                                    key={w.id}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '14px',
                                        padding: '4px 6px',
                                        borderTop: '1px solid var(--border-color)',
                                        fontSize: '12px', color: 'var(--text-secondary)',
                                    }}
                                >
                                    <span style={{ color: 'var(--accent-cyan)', fontWeight: 700, minWidth: '44px' }}>
                                        W{w.weekNumber}
                                    </span>
                                    <span style={{ minWidth: '52px' }}>
                                        {secondsToHours(w.secondsWorked).toFixed(2)}u
                                    </span>
                                    <span style={{
                                        padding: '1px 7px', borderRadius: '10px',
                                        background: 'rgba(139,92,246,0.15)', color: '#a78bfa',
                                        fontWeight: 600, minWidth: '40px', textAlign: 'center',
                                    }}>
                                        {w.weight}%
                                    </span>
                                    <span style={{ marginLeft: 'auto', color: 'var(--success)', fontWeight: 600 }}>
                                        → {totalHoursUpTo(sorted, w).toFixed(2)}u
                                    </span>
                                    <EditWeek weekData={w} link={link} />
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// ── ProjectListSimplified ─────────────────────────────────────────────────────

const ProjectListSimplified = () => {
    const { loginData, setLoginData } = useContext<IContextData>(StateContext);
    const [query, setQuery] = useState('');
    const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(new Set());
    const [modalState, setModalState] = useState<{ projectId: number; type: 'meerwerk' | 'dhz' } | null>(null);
    const [deleteOpen, setDeleteOpen] = useState<number | null>(null);
    const { t } = useLanguage();
    const isAdmin = loginData?.user?.isAdmin ?? false;

    const toggleMonth = (key: string) =>
        setCollapsedMonths(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });

    const handleMaterialsToggle = (p: Project) =>
        editProjectCall(p.id, p.name, p.estimatedTimeInSeconds, p.about, setLoginData, !p.materialsDelivered);

    const handleSaveExtras = (p: Project, type: 'meerwerk' | 'dhz', newHours: number) => {
        const meerwerkSeconds = type === 'meerwerk' ? hoursToSeconds(newHours) : (p.meerwerkSeconds ?? 0);
        const dhzSeconds = type === 'dhz' ? hoursToSeconds(newHours) : (p.dhzSeconds ?? 0);
        updateProjectExtrasCall(p.id, meerwerkSeconds, dhzSeconds, setLoginData);
    };

    const projects = (loginData.projects as Project[]).filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase())
    );

    const sortByMechanicName = (a, b) => a.mechanic.name.localeCompare(b.mechanic.name);

    const activeModal = modalState ? projects.find(p => p.id === modalState.projectId) : null;

    const renderMechanicsForType = (p: Project, typeId: number | null) => {
        const links = typeId === null
            ? (p.links ?? []).filter(link => link.weekData.some(w => w.projectTypeId == null))
            : (p.links ?? []).filter(link =>
                link.projectTypeId === typeId || link.weekData.some(w => w.projectTypeId === typeId)
              );
        return links.sort(sortByMechanicName).map(link => (
            <MechanicRow key={link.id} link={link} project={p} filterTypeId={typeId} />
        ));
    };

    const renderProject = (p: Project) => {
        const hasTypes = (p.types ?? []).length > 0;
        const estimatedHours = hasTypes
            ? (p.types ?? []).reduce((sum, pt) => sum + secondsToHours(pt.calculatedTimeInSeconds), 0)
            : secondsToHours(p.estimatedTimeInSeconds);
        const baseline = totalProjectBaseline(p.links);
        const remainingHours = estimatedHours - baseline;
        const meerwerkHours = secondsToHours(p.meerwerkSeconds ?? 0);
        const dhzHours = secondsToHours(p.dhzSeconds ?? 0);
        const untypedLinks = hasTypes
            ? (p.links ?? []).filter(link => link.weekData.some(w => w.projectTypeId == null))
            : [];

        return (
            <ExpandableRow key={p.id} Title={p.name} total={remainingHours}>
                <div style={{ padding: '0 14px 14px' }}>

                    {/* ── action bar ── */}
                    <div style={{
                        display: 'flex', flexDirection: 'column', gap: '8px',
                        padding: '10px 0 12px',
                        borderBottom: '1px solid var(--border-color)',
                        marginBottom: '12px',
                    }}>
                        {/* Row 1: status chips (left) + hour overview (right) */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                <button
                                    style={statusButton(p.materialsDelivered, '#00d9ff')}
                                    onClick={e => { e.stopPropagation(); handleMaterialsToggle(p); }}
                                >
                                    <span style={checkbox(p.materialsDelivered, '#00d9ff')}>
                                        {p.materialsDelivered ? '✓' : ''}
                                    </span>
                                    {t('materialsDelivered')}
                                </button>
                                <button
                                    style={statusButton(meerwerkHours > 0, '#1976d2')}
                                    onClick={e => { e.stopPropagation(); setModalState({ projectId: p.id, type: 'meerwerk' }); }}
                                >
                                    <span style={dot('#1976d2')} />
                                    {t('meerwerk')}
                                    {meerwerkHours > 0
                                        ? <span style={{ marginLeft: '2px', opacity: 0.85 }}>{meerwerkHours.toFixed(2)}u</span>
                                        : <span style={{ opacity: 0.6, fontWeight: 400 }}>+ toevoegen</span>}
                                </button>
                                <button
                                    style={statusButton(dhzHours > 0, '#2e7d32')}
                                    onClick={e => { e.stopPropagation(); setModalState({ projectId: p.id, type: 'dhz' }); }}
                                >
                                    <span style={dot('#2e7d32')} />
                                    {t('dhzWork')}
                                    {dhzHours > 0
                                        ? <span style={{ marginLeft: '2px', opacity: 0.85 }}>{dhzHours.toFixed(2)}u</span>
                                        : <span style={{ opacity: 0.6, fontWeight: 400 }}>+ toevoegen</span>}
                                </button>
                            </div>
                            <TotalHourOverview
                                estimatedTimeInSeconds={p.estimatedTimeInSeconds}
                                project={p}
                                link={p.links}
                            />
                        </div>

                        {/* Row 2: action buttons right-aligned */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', flexWrap: 'wrap' }}>
                            <PrintablePage project={p} link={p.links} />
                            <ToDoList project={p} link={p.links} />
                            <ConnectMechanic id={p.id} />
                            <EditProject
                                id={p.id}
                                name={p.name}
                                about={p.about}
                                estimatedTimeInSeconds={p.estimatedTimeInSeconds}
                                materialsDelivered={p.materialsDelivered}
                                types={p.types ?? []}
                            />
                            {isAdmin && p.createdByUserName && (
                                <span style={{ fontSize: '11px', color: 'var(--text-muted, #888)' }}>
                                    {p.createdByUserName}
                                </span>
                            )}
                            <Button
                                variant='outlined'
                                color='error'
                                size='small'
                                onClick={() => setDeleteOpen(p.id)}
                                    sx={{
                                        borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                                        padding: '4px 12px', minWidth: 'auto',
                                        borderColor: 'rgba(239,68,68,0.5)', color: '#ef4444', fontSize: '12px',
                                        '&:hover': { borderColor: '#ef4444', background: 'rgba(239,68,68,0.1)' },
                                    }}
                                >
                                    {t('delete')}
                                </Button>
                            <DeleteConfirmation
                                dialogOpen={deleteOpen === p.id}
                                handleDialogAbort={e => { e.stopPropagation(); setDeleteOpen(null); }}
                                handleDialogConfirm={() => { deleteProjectCall(p, setLoginData); setDeleteOpen(null); }}
                            />
                        </div>
                    </div>

                    {/* ── mechanics / type sections ── */}
                    {hasTypes ? (
                        <>
                            {(p.types ?? []).map(pt => {
                                const typeRemaining = secondsToHours(pt.calculatedTimeInSeconds) - hoursForType(p.links ?? [], pt.id);
                                const pos = typeRemaining >= 0;
                                return (
                                    <div key={pt.id} style={{ marginBottom: '10px' }}>
                                        <div style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            borderLeft: '3px solid var(--accent-cyan)',
                                            paddingLeft: '10px', marginBottom: '6px',
                                        }}>
                                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--accent-cyan)' }}>
                                                {pt.name}
                                            </span>
                                            <span style={{ fontSize: '12px', fontWeight: 700, color: pos ? 'var(--success)' : 'var(--danger)' }}>
                                                {typeRemaining.toFixed(2)}u
                                            </span>
                                        </div>
                                        {renderMechanicsForType(p, pt.id)}
                                    </div>
                                );
                            })}
                            {untypedLinks.length > 0 && (
                                <div style={{ marginBottom: '10px' }}>
                                    <div style={{
                                        borderLeft: '3px solid #555', paddingLeft: '10px', marginBottom: '6px',
                                        fontSize: '13px', fontWeight: 700, color: '#888',
                                    }}>
                                        {t('noType')}
                                    </div>
                                    {renderMechanicsForType(p, null)}
                                </div>
                            )}
                        </>
                    ) : (
                        (p.links ?? []).sort(sortByMechanicName).map(link => (
                            <MechanicRow key={link.id} link={link} project={p} />
                        ))
                    )}
                </div>
            </ExpandableRow>
        );
    };

    const renderGrouped = () => {
        const grouped = new Map<string, Project[]>();
        for (const p of projects) {
            const key = getProjectMonthKey(p);
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key)!.push(p);
        }
        const sortedKeys = Array.from(grouped.keys()).sort((a, b) => {
            if (a === 'no-data') return 1;
            if (b === 'no-data') return -1;
            return a.localeCompare(b);
        });
        return sortedKeys.map(key => {
            const collapsed = collapsedMonths.has(key);
            return (
                <div key={key}>
                    <div
                        onClick={() => toggleMonth(key)}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            fontSize: '13px', fontWeight: 700, color: 'var(--accent-cyan)',
                            textTransform: 'uppercase', letterSpacing: '0.08em',
                            padding: '16px 4px 6px',
                            borderBottom: '1px solid var(--border-color)',
                            marginBottom: collapsed ? '0' : '8px',
                            cursor: 'pointer', userSelect: 'none',
                        }}
                    >
                        {formatMonthKey(key)}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1976d2"
                            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transition: 'transform 0.2s ease', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', flexShrink: 0 }}
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </div>
                    {!collapsed && grouped.get(key)!.map(renderProject)}
                </div>
            );
        });
    };

    return (
        <div className='w-full'>
            <FilterProjectList query={query} setQuery={setQuery} />
            {renderGrouped()}
            {modalState && activeModal && (
                <ProjectExtrasModal
                    open={true}
                    onClose={() => setModalState(null)}
                    label={modalState.type === 'meerwerk' ? t('meerwerk') : t('dhzWork')}
                    accentColor={modalState.type === 'meerwerk' ? '#1976d2' : '#2e7d32'}
                    initialHours={
                        modalState.type === 'meerwerk'
                            ? secondsToHours(activeModal.meerwerkSeconds ?? 0)
                            : secondsToHours(activeModal.dhzSeconds ?? 0)
                    }
                    onSave={hours => handleSaveExtras(activeModal, modalState.type, hours)}
                />
            )}
        </div>
    );
};

export default ProjectListSimplified;
